import React, { useCallback, useState } from 'react';
import TeacherPlanUpgradePopup from '../../../components2/freemium/TeacherPlanUpgradePopup';
import {
  getCurrentPlan,
  getUpgradeOptions,
} from '../../../utils/userAccessHelper';
import { useProfile } from '../../state/hooks';
import { isBulkUploadPlanLocked } from '../rules';

export const BULK_UPLOAD_UPGRADE_TITLE =
  'Bulk upload is available on paid plans';
export const BULK_UPLOAD_UPGRADE_SUBTITLE =
  "Upload a whole class's responses at once and mark them together with Teacher Pro or a school plan.";

/**
 * The upgrade prompt a free-plan teacher gets instead of the bulk upload wizard.
 *
 * It is the ordinary teacher plan popup with bulk-upload copy, so the plans, the
 * checkout hand-off and the school-plan request are the same ones every other
 * upgrade path uses.
 */
export const BulkUploadUpgradePopup = ({ isOpen, onClose, profile }) => (
  <TeacherPlanUpgradePopup
    isOpen={isOpen}
    onClose={onClose}
    onPlanSelect={onClose}
    upgradeOptions={getUpgradeOptions(profile)}
    currentPlan={getCurrentPlan(profile)}
    profile={profile}
    eyebrow="Upgrade"
    title={BULK_UPLOAD_UPGRADE_TITLE}
    subtitle={BULK_UPLOAD_UPGRADE_SUBTITLE}
  />
);

/**
 * Wraps every "Bulk Upload" entry point in the plan check.
 *
 * `guard(start)` returns a click handler: on a plan that includes bulk upload
 * it calls `start` as before; on the free tier it opens the upgrade prompt and
 * never calls `start`, so no wizard, dialog or route opens that the backend
 * would refuse anyway. Render `popup` once alongside the entry point.
 */
export const useBulkUploadGate = () => {
  const { data: profile } = useProfile();
  const [isOpen, setIsOpen] = useState(false);
  const locked = isBulkUploadPlanLocked(profile);

  const openUpgrade = useCallback(() => setIsOpen(true), []);
  const closeUpgrade = useCallback(() => setIsOpen(false), []);

  const guard = useCallback(
    (start) =>
      (...args) => {
        if (locked) {
          setIsOpen(true);
          return undefined;
        }
        return start?.(...args);
      },
    [locked]
  );

  const popup = (
    <BulkUploadUpgradePopup
      isOpen={isOpen}
      onClose={closeUpgrade}
      profile={profile}
    />
  );

  return { locked, guard, popup, openUpgrade, closeUpgrade, isOpen };
};
